import { Select as AntdSelect, SelectProps as AntSelectProps } from 'antd';
import classNames from 'classnames';
import { useMemo, useRef, useCallback, useState, type JSX } from 'react';

import { SelectOption } from '@/shared/types';
import { Button, ButtonSizes, ButtonVariant, Icon, Typography } from '@/shared/ui';

import { FormInput, FormInputProps } from '../FormInput/FormInput';

import type { RefSelectProps } from 'antd/lib/select';

import './Select.scss';

export const CUSTOM_OPTION_ID = 'custom';

type InternalOption<
  OptionValue extends string = string,
  OptionType extends SelectOption<OptionValue> = SelectOption<OptionValue>,
> = OptionType & { value: OptionValue };

type GenericAntSelectProps<
  OptionValue extends string = string,
  OptionType extends SelectOption<OptionValue> = SelectOption<OptionValue>,
> = AntSelectProps<OptionValue, OptionType>;

interface SelectBaseProps<
  OptionValue extends string = string,
  OptionType extends SelectOption<OptionValue> = SelectOption<OptionValue>,
>
  extends FormInputProps, Omit<GenericAntSelectProps<OptionValue, OptionType>, 'onChange' | 'popupRender'> {
  options: NonNullable<GenericAntSelectProps<OptionValue, OptionType>['options']>;
  allowClear?: boolean;
  innerRef?: React.RefObject<RefSelectProps>;
  onChange?: (value: OptionValue, option: OptionType) => void;
  pinnedOptionLabel?: string;
}

interface PropsWithClear<
  OptionValue extends string = string,
  OptionType extends SelectOption<OptionValue> = SelectOption<OptionValue>,
> extends SelectBaseProps<OptionValue, OptionType> {
  allowClear: true;
  onChange?: (
    value: Nullable<OptionValue>,
    option: Nullable<SelectBaseProps<OptionValue, OptionType>['options'][number]>,
  ) => void;
}

interface PropsWithoutClear<
  OptionValue extends string = string,
  OptionType extends SelectOption<OptionValue> = SelectOption<OptionValue>,
> extends SelectBaseProps<OptionValue, OptionType> {
  allowClear?: false;
}

export type SelectProps<
  OptionValue extends string = string,
  OptionType extends SelectOption<OptionValue> = SelectOption<OptionValue>,
> = PropsWithClear<OptionValue, OptionType> | PropsWithoutClear<OptionValue, OptionType>;

export const Select = <
  OptionValue extends string = string,
  OptionType extends SelectOption<OptionValue> = SelectOption<OptionValue>,
>({
  label,
  errorMessage,
  popupClassName,
  className,
  options,
  placeholder = 'Виберіть варіант зі списку',
  value,
  required,
  allowClear = false,
  innerRef,
  onChange,
  onOpenChange,
  pinnedOptionLabel,
  ...props
}: SelectProps<OptionValue, OptionType>): JSX.Element => {
  const selectRef = useRef<RefSelectProps>(null);
  const ref = innerRef || selectRef;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const internalOptions = useMemo<Array<InternalOption<OptionValue, OptionType>>>(() => {
    return options.map<InternalOption<OptionValue, OptionType>>((option) => ({
      ...option,
      value: option.id as OptionValue,
    }));
  }, [options]);

  const handleDropdownVisibleChange = useCallback(
    (open: boolean) => {
      setIsDropdownOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange],
  );

  return (
    <FormInput label={label} required={required} errorMessage={errorMessage}>
      <AntdSelect
        ref={ref}
        showSearch={true}
        open={isDropdownOpen}
        allowClear={
          allowClear && value !== ''
            ? { clearIcon: <Icon name="close" color="grey" className="custom-select__clear-icon" /> }
            : false
        }
        // TODO: deal with ts Error #MVSUSLUH-3655
        // @ts-ignore
        filterOption={(input, option): boolean => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        optionRender={(option): React.ReactNode => (
          <Typography className="custom-select__option" as="span" color="black">
            {option.label}
          </Typography>
        )}
        {...props}
        data-testid="custom-select"
        className={classNames('custom-select', className)}
        classNames={{
          popup: {
            root: classNames('custom-select-popup', popupClassName),
          },
        }}
        placeholder={placeholder}
        status={errorMessage ? 'error' : undefined}
        suffixIcon={<Icon name="arrow_drop_down" color="grey" />}
        getPopupContainer={props.getPopupContainer ?? ((container): HTMLElement => container.parentElement)}
        placement="bottomLeft"
        dropdownAlign={{
          points: ['tl', 'bl'],
          offset: [0, 0],
          overflow: { adjustX: false, adjustY: false },
        }}
        popupMatchSelectWidth={true}
        options={internalOptions}
        value={value}
        onOpenChange={handleDropdownVisibleChange}
        popupRender={
          pinnedOptionLabel && onChange
            ? (menu) => (
                <>
                  {menu}
                  <div className="custom-select__pinned-option">
                    <Button
                      size={ButtonSizes.SM}
                      variant={ButtonVariant.SECONDARY}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        if (!onChange) return;
                        onChange(
                          CUSTOM_OPTION_ID as OptionValue,
                          { id: CUSTOM_OPTION_ID, label: pinnedOptionLabel } as OptionType,
                        );
                        selectRef.current?.blur();
                      }}
                    >
                      <span className="custom-select__option">{pinnedOptionLabel}</span>
                    </Button>
                  </div>
                </>
              )
            : undefined
        }
        onChange={(newValue, internalOption): void => {
          if (!onChange) return;

          if (newValue === undefined || newValue === null) {
            // @ts-ignore
            allowClear && onChange(undefined, undefined);
          } else if (!Array.isArray(internalOption)) {
            // @ts-ignore
            const { value: internalValue, ...option } = internalOption;
            internalValue !== value && onChange(internalValue, option as unknown as OptionType);
          }
        }}
      />
    </FormInput>
  );
};
